import { http, createConfig, fallback } from 'wagmi'
import { hardhat, arbitrum, polygon, bsc } from 'wagmi/chains'
import { connectorsForWallets } from '@rainbow-me/rainbowkit'
import {
  injectedWallet,
  rainbowWallet,
  walletConnectWallet,
  coinbaseWallet,
  trustWallet,
} from '@rainbow-me/rainbowkit/wallets'

// Get environment variables
const walletConnectProjectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || 'demo-project-id'
const rawAlchemyApiKey = import.meta.env.VITE_ALCHEMY_API_KEY
const rawInfuraApiKey = import.meta.env.VITE_INFURA_API_KEY

// Treat placeholder keys like "temp_*" or empty strings as "no key"
const normalizeKey = (key: string | undefined) => {
  if (!key) return undefined
  const trimmed = key.trim()
  if (!trimmed) return undefined
  if (trimmed.toLowerCase().startsWith('temp_')) return undefined
  return trimmed
}

const alchemyApiKey = normalizeKey(rawAlchemyApiKey)
const infuraApiKey = normalizeKey(rawInfuraApiKey)

// Configure supported chains - localhost, Arbitrum, Polygon and BSC mainnet
export const supportedChains = [
  hardhat, // localhost (chainId: 31337)
  arbitrum, // Arbitrum One mainnet (chainId: 42161)
  polygon, // Polygon mainnet (chainId: 137)
  bsc, // BNB Smart Chain mainnet (chainId: 56)
] as const

// Configure RPC URLs for each chain
const getRpcUrls = (chainId: number) => {
  const urls: string[] = []

  // For localhost (Hardhat)
  if (chainId === 31337) {
    urls.push('http://127.0.0.1:8545')
    return urls
  }

  // For Arbitrum One mainnet
  if (chainId === 42161) {
    if (alchemyApiKey) {
      urls.push(`https://arb-mainnet.g.alchemy.com/v2/${alchemyApiKey}`)
    }
    if (infuraApiKey) {
      urls.push(`https://arbitrum-mainnet.infura.io/v3/${infuraApiKey}`)
    }
    urls.push('https://arb1.arbitrum.io/rpc')
    urls.push('https://arbitrum-one-rpc.publicnode.com')
  }

  // For Polygon mainnet
  if (chainId === 137) {
    if (alchemyApiKey) {
      urls.push(`https://polygon-mainnet.g.alchemy.com/v2/${alchemyApiKey}`)
    }
    if (infuraApiKey) {
      urls.push(`https://polygon-mainnet.infura.io/v3/${infuraApiKey}`)
    }
    urls.push('https://polygon-rpc.com')
    urls.push('https://polygon-bor-rpc.publicnode.com')
  }

  // For BSC mainnet
  if (chainId === 56) {
    urls.push('https://bsc-rpc.publicnode.com')
    urls.push('https://bsc-dataseed.binance.org')
    urls.push('https://bsc-dataseed1.defibit.io')
  }

  return urls
}

// Create transports for all supported chains
// Use only HTTP transports to avoid WebSocket subscription errors
// IMPORTANT: Explicitly exclude thirdweb URLs
const transports: Record<number, ReturnType<typeof fallback>> = {}
supportedChains.forEach((chain) => {
  const rpcUrls = getRpcUrls(chain.id)
  
  // Filter out any thirdweb URLs (safety check)
  const filteredUrls = rpcUrls.filter(url => {
    const isThirdweb = url.includes('thirdweb') || url.includes('11155111.rpc.thirdweb.com')
    if (isThirdweb) {
      console.warn(`⚠️ Filtered out thirdweb RPC: ${url}`)
    }
    return !isThirdweb
  })
  
  if (filteredUrls.length === 0) {
    console.error(`❌ No valid RPC URLs for chain ${chain.id} after filtering`)
    return
  }
  
  console.log(`✅ Using ${filteredUrls.length} RPC URLs for chain ${chain.id}:`, filteredUrls)
  
  // Use HTTP transports only (no WebSocket to avoid subscription errors)
  // Public RPCs don't support WebSocket subscriptions
  const httpTransports = filteredUrls.map((url) =>
    http(url, {
      batch: {
        batchSize: 1024,
        wait: 50,
      },
      fetchOptions: {
        keepalive: false,
      },
      timeout: 30000, // 30 second timeout
    })
  )
  
  transports[chain.id] = fallback(httpTransports, {
    rank: false, // Don't rank transports, use first available
  })
})

// Configure wallet connectors
const connectors = connectorsForWallets(
  [
    {
      groupName: 'Recommended',
      wallets: [
        // Use generic injected wallet so that MetaMask in-app browser
        // (and browser extensions) are handled via window.ethereum
        // instead of MetaMask SDK, which does not work inside
        // MetaMask's own browser.
        injectedWallet,
        rainbowWallet,
        walletConnectWallet,
        coinbaseWallet,
      ],
    },
    {
      groupName: 'Other',
      wallets: [trustWallet],
    },
  ],
  {
    appName: 'NFTWebToken',
    projectId: walletConnectProjectId,
  }
)

// Create Wagmi config with multiple chains
// CRITICAL: Explicitly set transports to override any default RPC URLs
// The transports object MUST override chain.rpcUrls completely
export const config = createConfig({
  chains: supportedChains,
  connectors,
  // CRITICAL: transports override chain.rpcUrls - this prevents thirdweb from being used
  transports: {
    [hardhat.id]: transports[hardhat.id],
    [arbitrum.id]: transports[arbitrum.id],
    [polygon.id]: transports[polygon.id],
    [bsc.id]: transports[bsc.id],
  },
  ssr: false,
  // Disable WebSocket subscriptions to avoid connection errors
  batch: {
    multicall: true,
  },
  // IMPORTANT: Enable sync to automatically detect chain from wallet
  syncConnectedChain: true,
})

// Default chain (read-only network for dApp)
// Arbitrum One mainnet (42161)
export const defaultChainId = 42161
export const activeChain = supportedChains.find((chain) => chain.id === defaultChainId) || arbitrum

// Export helper to check if chain is supported
export const isSupportedNetwork = (chainId: number | undefined) => {
  if (!chainId) return false
  return supportedChains.some((chain) => chain.id === chainId)
}

// Export helper to get chain by ID
export const getChainById = (chainId: number) => {
  return supportedChains.find((chain) => chain.id === chainId)
}
